/**
 * State Map Search Plugin for Search Results Map Variant
 *
 * Manages the State SRP viewport-triggered search flow: re-fetching map markers
 * as the user pans or zooms the map when on a State search results page.
 *
 * Single responsibility: decide when and how a viewport-area search should be
 * triggered for State SRP based on map movement.
 *
 * This plugin does NOT:
 * - Create or configure the map instance (see map.plugin)
 * - Add coordinates to the URL (intentionally omitted for State SRP)
 * - Manage the "search within map" button (see searchWithinMap.plugin)
 * - Handle rectangular-area searches (see searchWithinMap.plugin)
 * - Pan the map to a new typeahead origin (see mapOriginSync.plugin)
 *
 * Coordinates URL parameter: For State SRP the locationType remains 'State'
 * throughout, so mapCoordinatesUrl.js (which only listens to RectangularArea)
 * never adds a `coordinates` query param to the URL.
 *
 * Reason to change: how the State SRP viewport-triggered fetch is triggered,
 * debounced, or how state bounds clamping works.
 *
 * @module searchResultsMap/plugins/viewportAutoSearch.plugin
 */

import {
  distinctUntilChanged,
  debounceTime,
  filter,
  map,
  pairwise,
  skip,
  take,
} from 'rxjs';
import {
  LocationType,
  searchParametersActions,
  selectLocationType,
  selectLocation,
} from 'features/searchResultsFacets';
import { MAP_GRID_SIZE } from 'libraries/mapLibrary.plugin';
import { selectGeoCoordinates, selectSitePlans } from '../redux/selectors';
import { mapBoundariesUpdated$ } from '../redux/observables';
import { isPolygonInBounds } from '../services/geometry';
import { SITE_PLAN_RENDER_ZOOM_THRESHOLD } from '../constants';

const STATE = LocationType.State.toString();

/**
 * Zoom level applied to the map when the user selects a different state from
 * the search bar. A zoom of 6 provides a state-wide view suitable for browsing
 * communities before panning into a specific area.
 */
const STATE_DEFAULT_ZOOM = 6;

/**
 * Returns true when the viewport and stateBounds rectangles have any geographic overlap.
 * Both arguments use the { east, north, south, west } shape (decimal degrees).
 */
const viewportOverlapsStateBounds = (viewport, stateBounds) =>
  viewport.south < stateBounds.north &&
  viewport.north > stateBounds.south &&
  viewport.west  < stateBounds.east  &&
  viewport.east  > stateBounds.west;

const areDifferentCoordinates = (previous, current) =>
  previous.east  !== current.east  ||
  previous.north !== current.north ||
  previous.south !== current.south ||
  previous.west  !== current.west;

/**
 * Returns the set of GRID_SIZE-degree grid cell keys that intersect the given bounds.
 * Uses the same bucketing algorithm as the marker spatial index so coverage checks
 * are consistent with how markers are stored.
 */
function getViewportGridCells(bounds) {
  const cells = [];
  const latMin = Math.floor(bounds.south / MAP_GRID_SIZE);
  const latMax = Math.ceil(bounds.north / MAP_GRID_SIZE);
  const lngMin = Math.floor(bounds.west / MAP_GRID_SIZE);
  const lngMax = Math.ceil(bounds.east / MAP_GRID_SIZE);

  Array.from({ length: latMax - latMin + 1 }, (_, i) => latMin + i).forEach((lat) => {
    Array.from({ length: lngMax - lngMin + 1 }, (_, i) => lngMin + i).forEach((lng) => {
      cells.push(`${lat},${lng}`);
    });
  });

  return cells;
}

/**
 * Checks if any grid cells intersecting the given bounds have not been marked as fetched.
 *
 * Used to determine whether a viewport-area fetch is necessary when the user pans/zooms
 * @param {Set<string>} fetchedCells
 * @param {{ south: number, north: number, west: number, east: number }} bounds
 * @returns {boolean}
 */
function viewportHasUnfetchedCells(fetchedCells, bounds) {
  return getViewportGridCells(bounds).some((cell) => !fetchedCells.has(cell));
}

/**
 * Marks all grid cells intersecting the given bounds as fetched by adding their keys to the set.
 * @param {Set<string>} fetchedCells
 * @param {{ south: number, north: number, west: number, east: number }} bounds
 */
function markViewportFetched(fetchedCells, bounds) {
  getViewportGridCells(bounds).forEach((cell) => fetchedCells.add(cell));
}

export default function createViewportAutoSearchPlugin() {
  const state = {
    started: false,
    subscriptions: [],
    mapFacade: null,
    store$: null,
    dispatch: null,
    getState: null,
    /** Stores the original state-wide bounding box so viewport moves can be clamped. */
    stateBounds: null,
    stateSelectionPending: false,
    /**
     * Tracks grid cells that have already been fetched for the current state.
     * Cleared whenever the location changes to ensure a fresh fetch for new areas.
     */
    fetchedCells: new Set(),
  };

  /**
   * Reads the current viewport coordinates, optionally clamps them to the state's
    * bounding box, then dispatches a map-only location update with locationType STATE.
   *
   * Because locationType is 'State' (not 'RectangularArea'), the mapCoordinatesUrl
   * routing layer will not add a `coordinates` query parameter to the browser URL.
   */
  function dispatchStateViewportFetch(currentCoordinates) {
    if (state.stateSelectionPending) {
      return;
    }

    const location = selectLocation(state.getState());
    if (!location) {
      return;
    }

    let { south, north, west, east } = currentCoordinates;

    if (state.stateBounds) {
      // Clamp viewport to state bounding box so the API never receives
      // coordinates outside the state the user is browsing.
      south = Math.max(south, state.stateBounds.south);
      north = Math.min(north, state.stateBounds.north);
      west  = Math.max(west,  state.stateBounds.west);
      east  = Math.min(east,  state.stateBounds.east);

      // Degenerate rectangle after clamping means the viewport has no overlap
      // with the state bounds — skip the dispatch.
      if (south >= north || west >= east) {
        return;
      }
    }

    const clampedBounds = { south, north, west, east };

    // Skip the map-only location update when every grid cell in the current viewport has
    // already been fetched for this state — the visible set cannot grow.
    // This optimisation is only relevant when the user is zoomed in enough for
    // interactive site plans to activate AND at least one ISP polygon overlaps
    // the current viewport. At lower zoom levels (or with no ISP in view) the
    // full fetch always runs so non-ISP results are never suppressed.
    const zoom = state.mapFacade?.getCameraSnapshot?.()?.zoom ?? 0;
    const sitePlans = selectSitePlans(state.getState()) ?? [];
    const hasInteractiveSitePlanInViewport =
      zoom >= SITE_PLAN_RENDER_ZOOM_THRESHOLD &&
      sitePlans.some((sp) => isPolygonInBounds(sp, clampedBounds));

    if (hasInteractiveSitePlanInViewport && !viewportHasUnfetchedCells(state.fetchedCells, clampedBounds)) {
      return;
    }

    if (hasInteractiveSitePlanInViewport) {
      markViewportFetched(state.fetchedCells, clampedBounds);
    }

    state.dispatch(
      searchParametersActions.fetchMapLocations({
        locationType: STATE,
        marketId: location.marketId,
        stateName: location.stateName,
        marketState: location.marketState,
        marketName: location.marketName,
        marketStateName: location.marketStateName,
        geoLocationState: location.geoLocationState,
        city: location.city,
        neighborhoodName: location.neighborhood,
        schoolDistrictName: location.schoolDistrictName,
        schoolDistrictId: location.schoolDistrictId,
        county: location.county,
        zip: location.zip,
        originLatitude: location.originLatitude,
        originLongitude: location.originLongitude,
        minimumLatitude: south,
        maximumLatitude: north,
        minimumLongitude: west,
        maximumLongitude: east,
      }),
    );
  }

  /**
   * Fallback bootstrap: reads the state bounding box directly from the Redux
   * location when the location type first transitions to State.
   *
   * This handles the edge case where the API response arrives after the user's
   * first pan/zoom (before subscribeToStateBoundsFromApiResponse fires).
   * Cleared automatically when the location type leaves State.
   */
  function subscribeToStateBoundsTracking() {
    state.subscriptions.push(
      state.store$.pipe(
        map(selectLocationType),
        distinctUntilChanged(),
      ).subscribe((locationType) => {
        if (locationType === STATE) {
          if (!state.stateBounds) {
            const location = selectLocation(state.getState());
            const south = parseFloat(location?.minimumLatitude);
            const north = parseFloat(location?.maximumLatitude);
            const west  = parseFloat(location?.minimumLongitude);
            const east  = parseFloat(location?.maximumLongitude);

            if ([south, north, west, east].every(Number.isFinite)) {
              state.stateBounds = { south, north, west, east };
            }
          }
        } else {
          state.stateBounds = null;
          state.fetchedCells.clear();
        }
      }),
    );
  }

  /**
   * Primary bootstrap: sets state.stateBounds from the first API response for
   * State SRP. The API carries the true state-wide bounding box (from
   * MapBoundaries.Min/MaxLatitude/Longitude), which is more reliable than the
   * initial Redux state (which may only reflect a zoom-12 viewport centroid).
   *
   * Subsequent responses (which carry narrower viewport bounds) are ignored via
   * the `state.stateBounds` guard to prevent the clamp window from shrinking.
   */
  function subscribeToStateBoundsFromApiResponse() {
    state.subscriptions.push(
      mapBoundariesUpdated$.subscribe((data) => {
        // Already bootstrapped — preserve the original state-wide bounds.
        if (state.stateBounds) {
          return;
        }

        // Only relevant for State SRP pages.
        if (selectLocationType(state.getState()) !== STATE) {
          return;
        }

        const bounds = data?.bounds;
        const south  = bounds?.minLatitude;
        const north  = bounds?.maxLatitude;
        const west   = bounds?.minLongitude;
        const east   = bounds?.maxLongitude;

        if ([south, north, west, east].every(Number.isFinite)) {
          state.stateBounds = { south, north, west, east };
        }
      }),
    );
  }

  /**
   * Resets state.stateBounds when the user navigates to a different state via
   * the search bar while locationType stays 'State' (distinctUntilChanged on
   * locationType means subscribeToStateBoundsTracking never re-fires, and the
   * subscribeToStateBoundsFromApiResponse guard prevents the new state's API
   * response from updating the bounds).
   *
   * Without this reset, state.stateBounds holds the previous state's bounding
   * box, so viewportOverlapsStateBounds always returns false for the new state's
   * viewport — silently blocking all subsequent map-interaction API calls.
   */
  function subscribeToLocationChange() {
    state.subscriptions.push(
      state.store$.pipe(
        filter((currentState) => selectLocationType(currentState) === STATE),
        map((currentState) => {
          const location = selectLocation(currentState);
          // Identify the state by fields that are stable across viewport fetches
          // (pans/zooms) but change when the user selects a different state via
          // the search bar.
          return [location?.stateName, location?.marketState,
            location?.originLatitude, location?.originLongitude].join('|');
        }),
        distinctUntilChanged(),
        skip(1), // skip the initial emission — first STATE load is not a location change
      ).subscribe(() => {
        // User navigated to a different state — clear stateBounds and fetchedCells
        // so the next API response can re-bootstrap the correct state-wide bounding
        // box and stale coverage data does not block fetches in the new state.
        state.stateBounds = null;
        state.fetchedCells.clear();
        state.stateSelectionPending = true;
        // take(1): reacts to only the first API response for the new state, suppressing
        // duplicate viewport fetches while that initial request is in flight. Without it
        // the subscription leaks and clears stateSelectionPending on every future response.
        state.subscriptions.push(
          mapBoundariesUpdated$.pipe(take(1)).subscribe(() => {
            state.stateSelectionPending = false;
          }),
        );

        // Re-centre the map on the new state's origin and reset to the
        // state-wide zoom level so the user immediately sees the full state.
        const location = selectLocation(state.getState());
        const lat = parseFloat(location?.originLatitude);
        const lng = parseFloat(location?.originLongitude);

        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          state.mapFacade?.updateMapCenter?.({ latitude: lat, longitude: lng });
          state.mapFacade?.setMapMinZoom?.(STATE_DEFAULT_ZOOM);
          state.mapFacade?.setMapZoom?.(STATE_DEFAULT_ZOOM);
        }
      }),
    );
  }

  /**
   * Resets the map to the state-wide zoom level when the location type
   * transitions from any non-State type to State (e.g. the user changes the
   * search bar from a city/zip/market entry to a state entry).
   *
   * subscribeToLocationChange handles State→different-State transitions via
   * distinctUntilChanged on the location identity key, but it intentionally
   * skips the very first State event with skip(1). This subscription covers
   * the complementary non-State→State case that skip(1) leaves unhandled.
   */
  function subscribeToNonStateToStateLocationTransition() {
    state.subscriptions.push(
      state.store$.pipe(
        map(selectLocationType),
        distinctUntilChanged(),
        pairwise(),
        filter(([previous, current]) => previous !== STATE && current === STATE),
      ).subscribe(() => {
        const location = selectLocation(state.getState());
        const lat = parseFloat(location?.originLatitude);
        const lng = parseFloat(location?.originLongitude);

        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          state.mapFacade?.updateMapCenter?.({ latitude: lat, longitude: lng });
          state.mapFacade?.setMapMinZoom?.(STATE_DEFAULT_ZOOM);
          state.mapFacade?.setMapZoom?.(STATE_DEFAULT_ZOOM);
        }
      }),
    );
  }

  /**
   * Re-fetches markers when the user pans or zooms on a State SRP page.
   *
   * Mirrors subscribeToMapMoves() in searchWithinMap.plugin but filtered for
   * STATE and dispatches with locationType STATE (never RectangularArea).
   */
  function subscribeToStateMapMoves() {
    state.subscriptions.push(
      state.store$.pipe(
        filter((currentState) => selectLocationType(currentState) === STATE),
        map(selectGeoCoordinates),
        pairwise(),
        filter(([previousCoordinates, currentCoordinates]) => previousCoordinates && currentCoordinates),
        filter(([previousCoordinates, currentCoordinates]) =>
          areDifferentCoordinates(previousCoordinates, currentCoordinates)),
        map(([, currentCoordinates]) => currentCoordinates),
        // Debounce to avoid excessive dispatches during continuous map movements,
        // while ensuring the last position is dispatched after movement ends.
        debounceTime(300),
        skip(1), // skip initial emission on subscription
      ).subscribe((currentCoordinates) => {
        if (!state.stateBounds || !viewportOverlapsStateBounds(currentCoordinates, state.stateBounds)) {
          return;
        }

        dispatchStateViewportFetch(currentCoordinates);
      }),
    );
  }

  function clearSubscriptions() {
    state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
    state.subscriptions = [];
  }

  return {
    name: 'viewport-auto-search',

    async init(context) {
      context.state.viewportAutoSearch = {
        started: false,
      };
    },

    async start(context) {
      if (state.started) {
        return;
      }

      state.mapFacade = context.services.mapFacade;
      state.store$ = context.services.store$;
      state.dispatch = context.services.dispatch;
      state.getState = context.services.getState;

      if (!state.mapFacade || !state.store$ || !state.dispatch || !state.getState) {
        return;
      }

      subscribeToStateBoundsTracking();
      subscribeToStateBoundsFromApiResponse();
      subscribeToLocationChange();
      subscribeToNonStateToStateLocationTransition();
      subscribeToStateMapMoves();

      state.started = true;
      context.state.viewportAutoSearch = {
        started: true,
      };
    },

    async stop(context) {
      clearSubscriptions();

      state.started = false;
      state.mapFacade = null;
      state.store$ = null;
      state.dispatch = null;
      state.getState = null;
      state.stateBounds = null;
      state.stateSelectionPending = false;
      state.fetchedCells.clear();

      delete context.state.viewportAutoSearch;
    },
  };
}
